package org.zq.astrock_ai.ui.research.gushi.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.border
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import org.zq.astrock_ai.api.dto.QuantScanItemDto
import org.zq.astrock_ai.storage.QuantScanPrefs
import org.zq.astrock_ai.ui.research.gushi.StockColors
import org.zq.astrock_ai.ui.research.gushi.adaptiveDialogWidth
import org.zq.astrock_ai.ui.research.gushi.data.ScanChangeKind
import org.zq.astrock_ai.ui.research.gushi.data.WatchlistGroupsStore
import org.zq.astrock_ai.ui.research.gushi.model.StockItem
import org.zq.astrock_ai.ui.research.gushi.model.TrendDirection
import org.zq.astrock_ai.util.formatDecimal

@Composable
fun WatchlistPanel(
    stocks: List<StockItem>,
    selectedCode: String,
    holdingCodes: Set<String> = emptySet(),
    scanByCode: Map<String, QuantScanItemDto> = emptyMap(),
    scanChanges: Map<String, ScanChangeKind> = emptyMap(),
    groupByCode: Map<String, String> = emptyMap(),
    scanSummary: String? = null,
    scanAlertText: String? = null,
    scanPrefs: QuantScanPrefs = QuantScanPrefs(),
    requestedFilter: String? = null,
    scanning: Boolean = false,
    onSelect: (String) -> Unit,
    onAddWatch: () -> Unit,
    onRemoveWatch: (String) -> Unit,
    onScanQuant: () -> Unit = {},
    onDismissAlert: () -> Unit = {},
    onAlertActivate: () -> Unit = {},
    onOpenHistory: () -> Unit = {},
    onOpenPortfolio: () -> Unit = {},
    onOpenPaperTrades: () -> Unit = {},
    onExportScan: () -> Unit = {},
    onOpenBackup: () -> Unit = {},
    layoutLabel: String = "自动",
    onCycleLayout: () -> Unit = {},
    onCycleGroup: (String) -> Unit = {},
    onGroupMapChange: (Map<String, String>) -> Unit = {},
    onScanPrefsChange: (QuantScanPrefs) -> Unit = {},
    onFilterRequestConsumed: () -> Unit = {},
    /** 手机端精简卡片，避免滑动卡顿 */
    phoneLayout: Boolean = false,
    browseStocks: List<StockItem> = emptyList(),
    paneTab: String = "自选",
    onPaneTabChange: (String) -> Unit = {},
    onRemoveBrowse: (String) -> Unit = {},
    modifier: Modifier = Modifier,
) {
    var filter by remember { mutableStateOf("全部") }
    var search by remember { mutableStateOf("") }
    var groupNames by remember { mutableStateOf(WatchlistGroupsStore.loadNames()) }
    var showAddGroup by remember { mutableStateOf(false) }
    var showManageGroups by remember { mutableStateOf(false) }

    LaunchedEffect(requestedFilter) {
        val target = requestedFilter ?: return@LaunchedEffect
        filter = target
        onFilterRequestConsumed()
    }

    val buyN = scanByCode.values.count { it.signal == "BUY" }
    val sellN = scanByCode.values.count { it.signal == "SELL" }
    val holdN = scanByCode.values.count { it.signal == "HOLD" }
    val changeN = scanChanges.size
    val browseMode = paneTab == "浏览记录"
    val sourceStocks = if (browseMode) browseStocks else stocks
    val visibleStocks = remember(sourceStocks, search, filter, scanByCode, scanChanges, groupByCode, holdingCodes, groupNames, browseMode) {
        sourceStocks.filter { stock ->
            val matchSearch = search.isBlank() ||
                stock.name.contains(search) ||
                stock.code.contains(search)
            if (browseMode) return@filter matchSearch
            val signal = scanByCode[stock.code]?.signal
            val group = WatchlistGroupsStore.groupOf(groupByCode, stock.code)
            val matchFilter = when {
                filter == "持仓" -> stock.code in holdingCodes
                filter == "买信号" -> signal == "BUY"
                filter == "卖信号" -> signal == "SELL"
                filter == "变化" -> stock.code in scanChanges
                filter in groupNames -> group == filter
                else -> true
            }
            matchSearch && matchFilter
        }
    }

    val listState = rememberLazyListState()

    // 整页单一 LazyColumn：手机可从顶部一直滑到底部，避免「只剩底部一小块能滑」
    LazyColumn(
        state = listState,
        modifier = modifier
            .fillMaxHeight()
            .background(Color(0xFFF8F9FB)),
        contentPadding = PaddingValues(horizontal = 12.dp, vertical = 12.dp),
        verticalArrangement = Arrangement.spacedBy(if (phoneLayout) 8.dp else 10.dp),
    ) {
        item(key = "header") {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    WatchlistPaneSwitch(
                        selected = paneTab,
                        onSelect = onPaneTabChange,
                        modifier = Modifier.weight(1f),
                    )
                    if (!browseMode) {
                        Text(
                            text = if (scanning) "扫描中" else "扫描",
                            color = if (scanning) StockColors.Neutral else Color.White,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            maxLines = 1,
                            softWrap = false,
                            modifier = Modifier
                                .background(
                                    if (scanning) Color(0xFFE5E7EB) else StockColors.PrimaryBlue,
                                    RoundedCornerShape(16.dp),
                                )
                                .clickable(enabled = !scanning, onClick = onScanQuant)
                                .padding(horizontal = 12.dp, vertical = 7.dp),
                        )
                    }
                }
            Text(
                    if (browseMode) "${browseStocks.size} 只最近看过" else "${stocks.size} 只标的",
                    color = StockColors.TextSecondary,
                    fontSize = 11.sp,
                    maxLines = 1,
                    softWrap = false,
                )
            }
        }

        if (!browseMode) {
            item(key = "actions") {
                LazyRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    item { WatchlistActionChip("组合", onClick = onOpenPortfolio) }
                    item { WatchlistActionChip("流水", onClick = onOpenPaperTrades) }
                    item { WatchlistActionChip("历史", onClick = onOpenHistory) }
                    item { WatchlistActionChip("导出", onClick = onExportScan) }
                    item { WatchlistActionChip("备份", onClick = onOpenBackup) }
                    item { WatchlistActionChip(layoutLabel, muted = true, onClick = onCycleLayout) }
                }
            }

            item(key = "prefs") {
                ScanPrefsRow(prefs = scanPrefs, onChange = onScanPrefsChange)
            }

            if (!scanAlertText.isNullOrBlank()) {
                item(key = "alert") {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(Color(0xFFFFF7ED), RoundedCornerShape(10.dp))
                            .clickable(onClick = onAlertActivate)
                            .padding(horizontal = 10.dp, vertical = 9.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Column(Modifier.weight(1f)) {
                            Text(
                                "信号变化提醒 · 点击查看",
                                color = Color(0xFFC2410C),
                                fontWeight = FontWeight.SemiBold,
                                fontSize = 12.sp,
                            )
                            Spacer(Modifier.height(2.dp))
                            Text(
                                scanAlertText,
                                color = StockColors.TextPrimary,
                                fontSize = 12.sp,
                                maxLines = 2,
                                overflow = TextOverflow.Ellipsis,
                            )
                        }
                        Text(
                            "知道了",
                            color = StockColors.PrimaryBlue,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            modifier = Modifier
                                .clickable(onClick = onDismissAlert)
                                .padding(horizontal = 6.dp, vertical = 4.dp),
                        )
                    }
                }
            }

            if (scanByCode.isNotEmpty() || !scanSummary.isNullOrBlank()) {
                item(key = "stats") {
                    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(Color.White, RoundedCornerShape(10.dp))
                                .padding(horizontal = 10.dp, vertical = 9.dp),
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                        ) {
                            ScanStatChip("买", buyN, StockColors.UpRed, StockColors.UpSoft, Modifier.weight(1f))
                            ScanStatChip("卖", sellN, StockColors.DownGreen, StockColors.DownSoft, Modifier.weight(1f))
                            ScanStatChip("观望", holdN, StockColors.TextSecondary, Color(0xFFF3F4F6), Modifier.weight(1f))
                            ScanStatChip(
                                "变化",
                                changeN,
                                StockColors.PrimaryBlue,
                                Color(0xFFE8F0FE),
                                Modifier.weight(1f),
                            )
                        }
                        if (!scanSummary.isNullOrBlank()) {
                            Text(scanSummary, color = StockColors.TextSecondary, fontSize = 11.sp, maxLines = 2)
                        }
                    }
                }
            }

            item(key = "filters") {
                LazyRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(5.dp),
                ) {
                    items(listOf("全部", "持仓", "买信号", "卖信号", "变化"), key = { it }) { item ->
                        SmallPill(
                            text = item,
                            selected = filter == item,
                            foreground = if (filter == item) Color.White else StockColors.TextSecondary,
                            background = if (filter == item) StockColors.PrimaryBlue else Color(0xFFF3F4F6),
                            onClick = { filter = item },
                        )
                    }
                }
            }

            item(key = "groups") {
                LazyRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(5.dp),
                ) {
                    items(groupNames, key = { it }) { item ->
                SmallPill(
                    text = item,
                    selected = filter == item,
                            foreground = if (filter == item) Color.White else StockColors.TextSecondary,
                            background = if (filter == item) StockColors.PrimaryBlue else Color(0xFFF3F4F6),
                            onClick = { filter = item },
                        )
                    }
                    item(key = "add-group") {
                        SmallPill(
                            text = "+分组",
                            selected = false,
                            foreground = StockColors.PrimaryBlue,
                            background = Color(0xFFE8F0FE),
                            onClick = { showAddGroup = true },
                        )
                    }
                    item(key = "manage-group") {
                        SmallPill(
                            text = "管理",
                            selected = false,
                            foreground = StockColors.TextSecondary,
                            background = Color(0xFFF3F4F6),
                            onClick = { showManageGroups = true },
                        )
                    }
                }
            }
        }

        item(key = "search") {
        SearchBox(
            value = search,
            onValueChange = { search = it },
                placeholder = if (browseMode) "搜索浏览记录" else "搜索自选股票",
                modifier = Modifier.fillMaxWidth(),
            )
        }

        if (visibleStocks.isEmpty()) {
            item(key = "empty") {
                Text(
                    if (browseMode) "暂无浏览记录，点荐股或自选会记在这里" else "暂无符合条件的自选",
                    color = StockColors.TextSecondary,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(vertical = 16.dp),
                )
            }
        }

        items(
            visibleStocks,
            key = { it.code },
            contentType = { if (phoneLayout) "watch_phone" else "watch" },
        ) { stock ->
                WatchStockCard(
                    stock = stock,
                    selected = stock.code == selectedCode,
                canRemove = if (browseMode) true else stocks.size > 1,
                scan = scanByCode[stock.code],
                change = if (browseMode) null else scanChanges[stock.code],
                group = if (browseMode) "" else WatchlistGroupsStore.groupOf(groupByCode, stock.code),
                phoneLayout = phoneLayout,
                onClick = { onSelect(stock.code) },
                onRemove = {
                    if (browseMode) onRemoveBrowse(stock.code) else onRemoveWatch(stock.code)
                },
                onCycleGroup = { if (!browseMode) onCycleGroup(stock.code) },
            )
        }

        if (!browseMode) {
            item(key = "add") {
                AddWatchCard(onClick = onAddWatch)
            }
        }
    }

    if (showAddGroup) {
        AddWatchlistGroupDialog(
            onDismiss = { showAddGroup = false },
            onConfirm = { name ->
                groupNames = WatchlistGroupsStore.addName(name)
                filter = name.trim()
                showAddGroup = false
            },
        )
    }
    if (showManageGroups) {
        ManageWatchlistGroupsDialog(
            names = groupNames,
            groupByCode = groupByCode,
            onDismiss = { showManageGroups = false },
            onNamesChange = { nextNames ->
                groupNames = nextNames
                if (filter !in nextNames && filter !in listOf("全部", "持仓", "买信号", "卖信号", "变化")) {
                    filter = "全部"
                }
            },
            onGroupMapChange = onGroupMapChange,
        )
    }
}

@Composable
private fun AddWatchlistGroupDialog(
    onDismiss: () -> Unit,
    onConfirm: (String) -> Unit,
) {
    var name by remember { mutableStateOf("") }
    val shape = RoundedCornerShape(14.dp)
    val fieldColors = OutlinedTextFieldDefaults.colors(
        focusedBorderColor = StockColors.PrimaryBlue,
        unfocusedBorderColor = StockColors.Border,
        focusedTextColor = StockColors.TextPrimary,
        unfocusedTextColor = StockColors.TextPrimary,
    )
    Dialog(onDismissRequest = onDismiss) {
        Column(
            modifier = Modifier
                .adaptiveDialogWidth(320.dp)
                .background(Color.White, shape)
                .padding(18.dp),
        ) {
            Text("新建分组", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = StockColors.TextPrimary)
            Spacer(Modifier.height(10.dp))
            OutlinedTextField(
                value = name,
                onValueChange = { name = it.take(8) },
                singleLine = true,
                label = { Text("分组名") },
                colors = fieldColors,
                modifier = Modifier.fillMaxWidth(),
            )
            Spacer(Modifier.height(14.dp))
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                Text(
                    "取消",
                    color = StockColors.TextSecondary,
                    modifier = Modifier
                        .clickable(onClick = onDismiss)
                        .padding(8.dp),
                )
                Spacer(Modifier.weight(1f))
                Text(
                    "添加",
                    color = Color.White,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier
                        .background(
                            if (name.trim().isNotBlank()) StockColors.PrimaryBlue else Color(0xFF9CA3AF),
                            RoundedCornerShape(10.dp),
                        )
                        .clickable(enabled = name.trim().isNotBlank()) { onConfirm(name.trim()) }
                        .padding(horizontal = 14.dp, vertical = 8.dp),
                )
            }
        }
    }
}

@Composable
private fun ManageWatchlistGroupsDialog(
    names: List<String>,
    groupByCode: Map<String, String>,
    onDismiss: () -> Unit,
    onNamesChange: (List<String>) -> Unit,
    onGroupMapChange: (Map<String, String>) -> Unit,
) {
    var renaming by remember { mutableStateOf<String?>(null) }
    var renameDraft by remember { mutableStateOf("") }
    val shape = RoundedCornerShape(14.dp)
    val fieldColors = OutlinedTextFieldDefaults.colors(
        focusedBorderColor = StockColors.PrimaryBlue,
        unfocusedBorderColor = StockColors.Border,
        focusedTextColor = StockColors.TextPrimary,
        unfocusedTextColor = StockColors.TextPrimary,
    )
    val manageable = names.filter { WatchlistGroupsStore.canManage(it) }
    Dialog(onDismissRequest = onDismiss) {
        Column(
            modifier = Modifier
                .adaptiveDialogWidth(360.dp)
                .heightIn(max = 480.dp)
                .background(Color.White, shape)
                .padding(18.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    "分组管理",
                    fontWeight = FontWeight.Bold,
                    fontSize = 16.sp,
                    color = StockColors.TextPrimary,
                    modifier = Modifier.weight(1f),
                )
                Text(
                    "关闭",
                    color = StockColors.PrimaryBlue,
                    fontSize = 13.sp,
                    modifier = Modifier.clickable(onClick = onDismiss).padding(6.dp),
                )
            }
            Spacer(Modifier.height(4.dp))
            Text("可重命名、排序、删除；删除后股票归入未分组", color = StockColors.TextSecondary, fontSize = 11.sp)
            Spacer(Modifier.height(12.dp))
            LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                items(manageable, key = { it }) { name ->
                    val count = groupByCode.values.count { it == name }
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(Color(0xFFF8F9FB), RoundedCornerShape(10.dp))
                            .padding(10.dp),
                    ) {
                        if (renaming == name) {
                            OutlinedTextField(
                                value = renameDraft,
                                onValueChange = { renameDraft = it.take(8) },
                                singleLine = true,
                                colors = fieldColors,
                                modifier = Modifier.fillMaxWidth(),
                            )
                            Spacer(Modifier.height(6.dp))
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                Text(
                                    "保存",
                                    color = StockColors.PrimaryBlue,
                                    fontWeight = FontWeight.SemiBold,
                                    fontSize = 12.sp,
                                    modifier = Modifier.clickable {
                                        val next = WatchlistGroupsStore.rename(name, renameDraft)
                                        val remapped = WatchlistGroupsStore.remapGroups(
                                            groupByCode, name, renameDraft.trim(),
                                        )
                                        onGroupMapChange(remapped)
                                        onNamesChange(next)
                                        renaming = null
                                    },
                                )
                                Text(
                                    "取消",
                                    color = StockColors.TextSecondary,
                                    fontSize = 12.sp,
                                    modifier = Modifier.clickable { renaming = null },
                                )
                            }
                        } else {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Text(
                                    "$name · ${count}只",
                                    color = StockColors.TextPrimary,
                                    fontWeight = FontWeight.SemiBold,
                                    fontSize = 13.sp,
                                    modifier = Modifier.weight(1f),
                                )
                                Text(
                                    "↑",
                                    color = StockColors.TextSecondary,
                                    fontSize = 16.sp,
                                    modifier = Modifier
                                        .defaultMinSize(minWidth = 40.dp, minHeight = 40.dp)
                                        .background(Color(0xFFF3F4F6), RoundedCornerShape(10.dp))
                                        .clickable {
                                            onNamesChange(WatchlistGroupsStore.moveName(name, -1))
                                        }
                                        .padding(horizontal = 10.dp, vertical = 8.dp),
                                )
                                Spacer(Modifier.width(4.dp))
                                Text(
                                    "↓",
                                    color = StockColors.TextSecondary,
                                    fontSize = 16.sp,
                                    modifier = Modifier
                                        .defaultMinSize(minWidth = 40.dp, minHeight = 40.dp)
                                        .background(Color(0xFFF3F4F6), RoundedCornerShape(10.dp))
                                        .clickable {
                                            onNamesChange(WatchlistGroupsStore.moveName(name, 1))
                                        }
                                        .padding(horizontal = 10.dp, vertical = 8.dp),
                                )
                                Spacer(Modifier.width(4.dp))
                                Text(
                                    "改",
                                    color = StockColors.PrimaryBlue,
                                    fontSize = 13.sp,
                                    fontWeight = FontWeight.SemiBold,
                                    modifier = Modifier
                                        .defaultMinSize(minWidth = 40.dp, minHeight = 40.dp)
                                        .background(Color(0xFFE8F0FE), RoundedCornerShape(10.dp))
                                        .clickable {
                                            renaming = name
                                            renameDraft = name
                                        }
                                        .padding(horizontal = 10.dp, vertical = 8.dp),
                                )
                                Spacer(Modifier.width(4.dp))
                                Text(
                                    "删",
                                    color = StockColors.UpRed,
                                    fontSize = 13.sp,
                                    fontWeight = FontWeight.SemiBold,
                                    modifier = Modifier
                                        .defaultMinSize(minWidth = 40.dp, minHeight = 40.dp)
                                        .background(Color(0xFFFDECEC), RoundedCornerShape(10.dp))
                                        .clickable {
                                            val remapped = WatchlistGroupsStore.remapGroups(
                                                groupByCode, name, null,
                                            )
                                            onGroupMapChange(remapped)
                                            onNamesChange(WatchlistGroupsStore.removeName(name))
                                        }
                                        .padding(horizontal = 10.dp, vertical = 8.dp),
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun WatchlistPaneSwitch(
    selected: String,
    onSelect: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier
            .background(Color(0xFFE5E7EB), RoundedCornerShape(10.dp))
            .padding(3.dp),
        horizontalArrangement = Arrangement.spacedBy(3.dp),
    ) {
        listOf("自选", "浏览记录").forEach { item ->
            val on = selected == item
            Box(
                modifier = Modifier
                    .weight(1f)
                    .background(
                        if (on) Color.White else Color.Transparent,
                        RoundedCornerShape(8.dp),
                    )
                    .clickable { onSelect(item) }
                    .padding(vertical = 7.dp),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    item,
                    color = if (on) StockColors.TextPrimary else StockColors.TextSecondary,
                    fontSize = 13.sp,
                    fontWeight = if (on) FontWeight.SemiBold else FontWeight.Medium,
                    maxLines = 1,
                    softWrap = false,
                )
            }
        }
    }
}

@Composable
private fun WatchlistActionChip(
    text: String,
    muted: Boolean = false,
    onClick: () -> Unit,
) {
    Text(
        text = text,
        color = if (muted) StockColors.TextSecondary else StockColors.PrimaryBlue,
        fontSize = 11.sp,
        fontWeight = FontWeight.SemiBold,
        maxLines = 1,
        softWrap = false,
        modifier = Modifier
            .background(
                if (muted) Color(0xFFF3F4F6) else Color(0xFFE8F0FE),
                RoundedCornerShape(14.dp),
            )
            .clickable(onClick = onClick)
            .padding(horizontal = 10.dp, vertical = 6.dp),
    )
}

@Composable
private fun ScanPrefsRow(
    prefs: QuantScanPrefs,
    onChange: (QuantScanPrefs) -> Unit,
) {
    LazyRow(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color.White, RoundedCornerShape(10.dp))
            .padding(horizontal = 8.dp, vertical = 7.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        item {
            Text(
                if (prefs.alertEnabled) "提醒开" else "提醒关",
                color = if (prefs.alertEnabled) StockColors.PrimaryBlue else StockColors.TextSecondary,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                softWrap = false,
                modifier = Modifier
                    .background(
                        if (prefs.alertEnabled) Color(0xFFE8F0FE) else Color(0xFFF3F4F6),
                        RoundedCornerShape(12.dp),
                    )
                    .clickable { onChange(prefs.copy(alertEnabled = !prefs.alertEnabled)) }
                    .padding(horizontal = 8.dp, vertical = 4.dp),
            )
        }
        item {
            Text("复扫", color = StockColors.TextSecondary, fontSize = 11.sp, maxLines = 1, softWrap = false)
        }
        items(listOf(0 to "关", 5 to "5分", 15 to "15分"), key = { it.first }) { (minutes, label) ->
            val selected = prefs.autoRescanMinutes == minutes
            Text(
                label,
                color = if (selected) Color.White else StockColors.TextSecondary,
                fontSize = 11.sp,
                fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
                maxLines = 1,
                softWrap = false,
                modifier = Modifier
                    .background(
                        if (selected) StockColors.PrimaryBlue else Color(0xFFF3F4F6),
                        RoundedCornerShape(12.dp),
                    )
                    .clickable { onChange(prefs.copy(autoRescanMinutes = minutes)) }
                    .padding(horizontal = 8.dp, vertical = 4.dp),
            )
        }
    }
}

@Composable
private fun ScanStatChip(
    label: String,
    count: Int,
    color: Color,
    background: Color,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .background(background, RoundedCornerShape(8.dp))
            .padding(vertical = 6.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text("$count", color = color, fontWeight = FontWeight.Bold, fontSize = 16.sp)
        Text(label, color = color, fontSize = 11.sp)
    }
}

@Composable
private fun WatchStockCard(
    stock: StockItem,
    selected: Boolean,
    canRemove: Boolean,
    scan: QuantScanItemDto?,
    change: ScanChangeKind?,
    group: String,
    phoneLayout: Boolean,
    onClick: () -> Unit,
    onRemove: () -> Unit,
    onCycleGroup: () -> Unit,
) {
    val trendColor = when (stock.direction) {
        TrendDirection.UP -> StockColors.UpRed
        TrendDirection.DOWN -> StockColors.DownGreen
        TrendDirection.FLAT -> StockColors.Neutral
    }
    val signalColor = when (scan?.signal) {
        "BUY" -> StockColors.UpRed
        "SELL" -> StockColors.DownGreen
        else -> StockColors.Neutral
    }
    val changeColor = when (change) {
        ScanChangeKind.NEW_BUY, ScanChangeKind.TO_BUY -> StockColors.UpRed
        ScanChangeKind.NEW_SELL, ScanChangeKind.TO_SELL -> StockColors.DownGreen
        null -> StockColors.Neutral
    }
    // 仅用扫描结果，卡片内不打网、不改 state，保证滑动帧率
    val sellZone = resolveWatchSellZone(scan)
    val buyZone = resolveWatchBuyZone(scan)
    val stripeColor = if (scan != null) signalColor else trendColor
    val bg = when {
        change != null -> Color(0xFFFFF8F0)
        selected -> StockColors.UpSoft
        else -> Color.White
    }
    val borderColor = when {
        selected -> StockColors.UpRed
        change != null -> Color(0xFFFBBF24)
        else -> StockColors.Border
    }
    val shape = RoundedCornerShape(if (phoneLayout) 10.dp else 11.dp)

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .graphicsLayer()
            .background(bg, shape)
            .border(
                width = if (selected || change != null) 1.5.dp else 1.dp,
                color = borderColor,
                shape = shape,
            )
            .clickable(onClick = onClick)
            .drawBehind {
                drawRect(color = stripeColor, size = Size(4.dp.toPx(), size.height))
            }
            .padding(
                start = 14.dp,
                end = if (phoneLayout) 10.dp else 12.dp,
                top = if (phoneLayout) 10.dp else 12.dp,
                bottom = if (phoneLayout) 10.dp else 12.dp,
            ),
        verticalArrangement = Arrangement.spacedBy(if (phoneLayout) 4.dp else 5.dp),
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        stock.name,
                        color = StockColors.TextPrimary,
                fontSize = if (phoneLayout) 15.sp else 16.sp,
                        fontWeight = FontWeight.SemiBold,
                        maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f),
                    )
                    Text(
                        stock.price,
                        color = trendColor,
                        fontWeight = FontWeight.Bold,
                fontSize = if (phoneLayout) 15.sp else 16.sp,
                maxLines = 1,
                    )
            Spacer(Modifier.size(8.dp))
                    Text(
                        stock.changePercent,
                        color = trendColor,
                        fontWeight = FontWeight.Bold,
                fontSize = if (phoneLayout) 14.sp else 15.sp,
                maxLines = 1,
                    )
                }

                Row(verticalAlignment = Alignment.CenterVertically) {
            Text(stock.code, color = StockColors.TextSecondary, fontSize = 12.sp, maxLines = 1)
            if (scan != null) {
                Spacer(Modifier.size(6.dp))
                Text(
                    when (scan.signal) {
                        "BUY" -> "买"
                        "SELL" -> "卖"
                        else -> "观望"
                    },
                    color = signalColor,
                    fontSize = 10.sp,
                    fontWeight = FontWeight.Bold,
                    maxLines = 1,
                    modifier = Modifier
                        .background(
                            when (scan.signal) {
                                "BUY" -> StockColors.UpSoft
                                "SELL" -> StockColors.DownSoft
                                else -> Color(0xFFF3F4F6)
                            },
                            RoundedCornerShape(6.dp),
                        )
                        .padding(horizontal = 5.dp, vertical = 1.dp),
                )
            }
            Spacer(Modifier.size(6.dp))
            if (group.isNotBlank()) {
                Text(
                    group,
                    color = StockColors.PrimaryBlue,
                    fontSize = 10.sp,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1,
                    modifier = Modifier
                        .background(Color(0xFFE8F0FE), RoundedCornerShape(6.dp))
                        .clickable(onClick = onCycleGroup)
                        .padding(horizontal = 5.dp, vertical = 1.dp),
                )
            }
            if (change != null) {
                Spacer(Modifier.size(6.dp))
                Text(
                    change.label,
                    color = Color.White,
                    fontSize = 10.sp,
                    fontWeight = FontWeight.Bold,
                    maxLines = 1,
                    modifier = Modifier
                        .background(changeColor, RoundedCornerShape(6.dp))
                        .padding(horizontal = 5.dp, vertical = 1.dp),
                )
            }
                    Spacer(Modifier.weight(1f))
            if (!phoneLayout) {
                    Sparkline(
                        values = stock.sparkline,
                        color = trendColor,
                    modifier = Modifier.size(width = 88.dp, height = 22.dp),
                )
            }
            if (canRemove) {
                if (!phoneLayout) Spacer(Modifier.size(8.dp))
                Text(
                    "移除",
                    color = StockColors.TextSecondary,
                    fontSize = 11.sp,
                    modifier = Modifier
                        .clickable(onClick = onRemove)
                        .padding(horizontal = 4.dp, vertical = 2.dp),
                )
            }
        }

        if (phoneLayout) {
            if (buyZone != null || sellZone != null) {
                Text(
                    buildString {
                        if (buyZone != null) {
                            append("买 ${formatDecimal(buyZone.first)}–${formatDecimal(buyZone.second)}")
                        }
                        if (buyZone != null && sellZone != null) append("  ·  ")
                        if (sellZone != null) {
                            append("卖 ${formatDecimal(sellZone.first)}–${formatDecimal(sellZone.second)}")
                        }
                    },
                    color = StockColors.TextSecondary,
                    fontSize = 11.sp,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        } else {
            if (scan != null) {
                val vote = if (scan.buyVotes + scan.sellVotes > 0) {
                    "买${scan.buyVotes}/卖${scan.sellVotes}"
                } else {
                    scan.reason.take(18)
                }
                Text(vote, color = signalColor, fontSize = 12.sp, maxLines = 1)
            }
            if (buyZone != null) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(StockColors.UpSoft, RoundedCornerShape(8.dp))
                        .padding(horizontal = 8.dp, vertical = 5.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text("建议买入", color = StockColors.UpRed, fontSize = 11.sp, fontWeight = FontWeight.SemiBold)
                    Spacer(Modifier.weight(1f))
                    Text(
                        "${formatDecimal(buyZone.first)}–${formatDecimal(buyZone.second)}",
                        color = StockColors.UpRed,
                        fontSize = 13.sp,
                        fontWeight = FontWeight.Bold,
                    )
                }
            }
            if (sellZone != null) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(StockColors.DownSoft, RoundedCornerShape(8.dp))
                        .padding(horizontal = 8.dp, vertical = 5.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text("建议卖出", color = StockColors.DownGreen, fontSize = 11.sp, fontWeight = FontWeight.SemiBold)
                    Spacer(Modifier.weight(1f))
                    Text(
                        "${formatDecimal(sellZone.first)}–${formatDecimal(sellZone.second)}",
                        color = StockColors.DownGreen,
                        fontSize = 13.sp,
                        fontWeight = FontWeight.Bold,
                    )
                }
            }
        }
    }
}

/** 优先用扫描返回的卖区。 */
private fun resolveWatchSellZone(scan: QuantScanItemDto?): Pair<Double, Double>? {
    if (scan != null && scan.sellLow > 0 && scan.sellHigh > 0) {
        return scan.sellLow to scan.sellHigh
    }
    return null
}

/** 优先用扫描返回的买区。 */
private fun resolveWatchBuyZone(scan: QuantScanItemDto?): Pair<Double, Double>? {
    if (scan != null && scan.buyLow > 0 && scan.buyHigh > 0) {
        return scan.buyLow to scan.buyHigh
    }
    return null
}

@Composable
private fun Sparkline(
    values: List<Float>,
    color: Color,
    modifier: Modifier = Modifier,
) {
    Canvas(modifier.graphicsLayer()) {
        if (values.size < 2) return@Canvas
        val path = Path()
        val last = values.lastIndex.coerceAtLeast(1)
        values.forEachIndexed { index, value ->
            val x = size.width * index / last
            val y = size.height * (1f - value.coerceIn(0f, 1f))
            if (index == 0) path.moveTo(x, y) else path.lineTo(x, y)
        }
        drawPath(path, color = color, style = androidx.compose.ui.graphics.drawscope.Stroke(width = 1.6f))
    }
}

@Composable
private fun AddWatchCard(onClick: () -> Unit) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .height(62.dp)
            .clickable(onClick = onClick),
        shape = RoundedCornerShape(10.dp),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, StockColors.Border),
        elevation = CardDefaults.cardElevation(0.dp),
    ) {
        Row(
            modifier = Modifier.fillMaxHeight(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center,
        ) {
            Text("+", color = StockColors.TextSecondary, fontSize = 18.sp)
            Spacer(Modifier.size(7.dp))
            Text("添加自选", color = StockColors.TextSecondary, fontSize = 14.sp)
        }
    }
}
